<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']" class="h-full">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            {{ specialty ? 'Edit Specialty' : 'Add Specialty' }}
                        </h3>
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="name_en" label="English name">
                        <UInput v-model="state.name_en" type="text" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="name_ar" label="Arabic name">
                        <UInput v-model="state.name_ar" type="text" size="xl" dir="rtl" />
                    </UFormGroup>
                    <UFormGroup name="icon" label="Icon" help="Optional Phosphor icon name, e.g. i-ph-heartbeat-duotone">
                        <UInput v-model="state.icon" type="text" size="xl" placeholder="i-ph-heartbeat-duotone">
                            <template v-if="state.icon" #trailing>
                                <UIcon :name="state.icon" class="size-5 text-gray-500" />
                            </template>
                        </UInput>
                    </UFormGroup>
                    <UFormGroup name="active" label="Active">
                        <UToggle v-model="state.active" />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" :loading="loading" color="black">
                            Save
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import type { Specialty } from '~/types/telemedicine'
import { countPublishedDoctors, specialtyInUseMessage } from '~/utils/telemedicineDoctor'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
    specialty?: Specialty
    nextOrder: number
}>()

const client = useSupabaseClient()
const toast = useToast()

const form = ref()
const state = ref<{ name_en?: string, name_ar?: string, icon?: string, active: boolean }>({ active: true })
const loading = ref<boolean>(false)

const schema = z.object({
    name_en: z.string().trim().min(1, 'Required'),
    name_ar: z.string().trim().min(1, 'Required'),
    icon: z.string().trim().optional(),
    active: z.boolean(),
})

watch(() => props.open, (open) => {
    if (open) {
        form.value?.clear()
        state.value = {
            name_en: props.specialty?.name_en,
            name_ar: props.specialty?.name_ar,
            icon: props.specialty?.icon ?? undefined,
            active: props.specialty?.active ?? true,
        }
    }
})

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    loading.value = true

    if (props.specialty?.active && !event.data.active) {
        const { count, error } = await countPublishedDoctors(client, props.specialty.id)
        if (error || count) {
            loading.value = false
            form.value.setErrors([{ path: 'active', message: error ? `Could not check doctors using this specialty: ${error.message}` : specialtyInUseMessage(count) }])
            return
        }
    }

    const payload = {
        name_en: event.data.name_en,
        name_ar: event.data.name_ar,
        icon: event.data.icon || null,
        active: event.data.active,
    }

    const { error } = props.specialty
        ? await client.from('specialties').update(payload as never).eq('id', props.specialty.id)
        : await client.from('specialties').insert({ ...payload, sort_order: props.nextOrder } as never)

    loading.value = false

    if (error) {
        if (error.code === '23505') {
            form.value.setErrors([
                { path: 'name_en', message: 'A specialty with this English name already exists' }
            ])
        } else {
            toast.add({ title: 'Could not save specialty', description: error.message, color: 'red' })
        }
        return
    }

    dismiss(true)
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload', true)
    }
}
</script>
